import * as React from 'react'
import clsx from 'clsx'
import { FormControlProps, FormControl } from '../FormControl'
import { FormHelperTextProps, FormHelperText } from '../FormHelperText'
import { InputLabelProps, InputLabel } from '../InputLabel'
import { InputBaseProps } from '../InputBase'
import { SxProps } from '../sx'
import { StandardInput, StandardInputProps } from '../StandardInput'
import { OutlinedInput, OutlinedInputProps } from '../OutlinedInput'
import { OutlinedDashInput, OutlinedDashInputProps } from '../OutlinedDashInput'
import { FilledInput, FilledInputProps } from '../FilledInput'
import { Select, SelectProps } from '../Select'
import { SelectAdvance, SelectAdvanceProps } from '../SelectAdvance'
import { useId } from '../../hooks'

export interface TextFieldClasses {
  /** Styles applied to the root element. */
  root: string
}

export type BaseTextFieldProps<T> = Omit<
  FormControlProps,
  // event handlers are declared on derived interfaces
  'onBlur' | 'onFocus' | 'defaultValue' | 'onChange'
> &
  (
    | {
        /**
         * Render a [`Select`](/material-ui/api/select/) element while passing the Input element to `Select` as `input` parameter.
         * If this option is set you must pass the options of the select as children.
         * @default false
         */
        select?: boolean
        /**
         * Props applied to the [`Select`](/material-ui/api/select/) element.
         */
        SelectProps?: Partial<SelectProps>
        /**
         * The value of the `input` element, required for a controlled component.
         */
        value?: unknown
        onChange?: InputBaseProps['onChange']
      }
    | {
        select: 'advanceSelect'
        SelectProps: Omit<SelectAdvanceProps<T>, 'value' | 'onChange'>
        value?: SelectAdvanceProps<T>['value']
        onChange?: SelectAdvanceProps<T>['onChange']
      }
  ) & {
    /**
     * This prop helps users to fill forms faster, especially on mobile devices.
     * The name can be confusing, as it's more like an autofill.
     * You can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill).
     */
    autoComplete?: string
    /**
     * If `true`, the `input` element is focused during the first mount.
     * @default false
     */
    autoFocus?: boolean
    /**
     * @ignore
     */
    children?: React.ReactNode
    /**
     * Override or extend the styles applied to the component.
     */
    classes?: Partial<TextFieldClasses>
    /**
     * The default value. Use when the component is not controlled.
     */
    defaultValue?: unknown
    /**
     * The color of the component.
     * @default 'primary'
     */
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
    /**
     * If `true`, the component is disabled.
     * @default false
     */
    disabled?: boolean
    /**
     * If `true`, the label is displayed in an error state.
     * @default false
     */
    error?: boolean
    /**
     * Props applied to the [`FormHelperText`](/material-ui/api/form-helper-text/) element.
     */
    FormHelperTextProps?: Partial<FormHelperTextProps>
    /**
     * If `true`, the input will take up the full width of its container.
     * @default false
     */
    fullWidth?: boolean
    /**
     * The helper text content.
     */
    helperText?: React.ReactNode
    /**
     * The id of the `input` element.
     * Use this prop to make `label` and `helperText` accessible for screen readers.
     */
    id?: string
    /**
     * Props applied to the [`InputLabel`](/material-ui/api/input-label/) element.
     * Pointer events like `onClick` are enabled if and only if `shrink` is `true`.
     */
    InputLabelProps?: Partial<InputLabelProps>
    /**
     * Props applied to the Input element.
     * It will be a [`FilledInput`](/material-ui/api/filled-input/),
     * [`OutlinedInput`](/material-ui/api/outlined-input/) or [`Input`](/material-ui/api/input/)
     * component depending on the `variant` prop value.
     */
    InputProps?:
      | StandardInputProps
      | OutlinedInputProps
      | OutlinedDashInputProps
      | FilledInputProps
    /**
     * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
     */
    inputProps?: InputBaseProps['inputProps']
    /**
     * Pass a ref to the `input` element.
     */
    inputRef?: React.Ref<any>
    /**
     * The label content.
     */
    label?: React.ReactNode
    /**
     * If `true`, a `textarea` element is rendered instead of an input.
     * @default false
     */
    multiline?: boolean
    /**
     * Name attribute of the `input` element.
     */
    name?: string
    onBlur?: InputBaseProps['onBlur']
    onFocus?: InputBaseProps['onFocus']
    /**
     * The short hint displayed in the `input` before the user enters a value.
     */
    placeholder?: string
    /**
     * If `true`, the label is displayed as required and the `input` element is required.
     * @default false
     */
    required?: boolean
    /**
     * Number of rows to display when multiline option is set to true.
     */
    rows?: string | number
    /**
     * Maximum number of rows to display when multiline option is set to true.
     */
    maxRows?: string | number
    /**
     * Minimum number of rows to display when multiline option is set to true.
     */
    minRows?: string | number
    /**
     * The size of the component.
     * @default medium
     */
    size?: 'small' | 'medium' | 'large'
    /**
     * The system prop that allows defining system overrides as well as additional CSS styles.
     */
    sx?: SxProps
    /**
     * Type of the `input` element. It should be [a valid HTML5 input type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types).
     */
    type?: React.InputHTMLAttributes<unknown>['type']
  }

const variantComponent = {
  standard: StandardInput,
  filled: FilledInput,
  outlined: OutlinedInput,
  outlinedDash: OutlinedDashInput,
}

/**
 * The `TextField` is a convenience wrapper for the most common cases (80%).
 * It cannot be all things to all people, otherwise the API would grow out of control.
 *
 * ## Advanced Configuration
 *
 * It's important to understand that the text field is a simple abstraction
 * on top of the following components:
 *
 * - [FormControl](/material-ui/api/form-control/)
 * - [InputLabel](/material-ui/api/input-label/)
 * - [FilledInput](/material-ui/api/filled-input/)
 * - [OutlinedInput](/material-ui/api/outlined-input/)
 * - [Input](/material-ui/api/input/)
 * - [FormHelperText](/material-ui/api/form-helper-text/)
 *
 * If you wish to alter the props applied to the `input` element, you can do so as follows:
 *
 * ```jsx
 * const inputProps = {
 *   step: 300,
 * };
 *
 * return <TextField id="time" type="time" inputProps={inputProps} />;
 * ```
 *
 * For advanced cases, please look at the source of TextField by clicking on the
 * "Edit this page" button above. Consider either:
 *
 * - using the upper case props for passing values directly to the components
 * - using the underlying components directly as shown in the demos
 */

function TextFieldFn<T>(
  props: BaseTextFieldProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const {
    autoComplete,
    classes,
    autoFocus = false,
    children,
    className,
    color = 'primary',
    defaultValue,
    disabled = false,
    error = false,
    FormHelperTextProps,
    fullWidth = false,
    helperText,
    id: idOverride,
    InputLabelProps,
    inputProps,
    InputProps,
    inputRef,
    label,
    maxRows,
    minRows,
    multiline = false,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    required = false,
    rows,
    select = false,
    SelectProps,
    type,
    value,
    variant = 'outlined',
    ...other
  } = props

  if (process.env.NODE_ENV !== 'production') {
    if (select && select !== 'advanceSelect' && !children) {
      console.error(
        '`children` must be passed when using the `TextField` component with `select`.'
      )
    }
  }

  const InputMore: any = {}

  if (select && select !== 'advanceSelect') {
    // unset defaults from textbox inputs
    if (!SelectProps || !(SelectProps as SelectProps).native) {
      InputMore.id = undefined
    }
    InputMore['aria-describedby'] = undefined
  }

  const id = useId(idOverride)
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined
  const inputLabelId = label && id ? `${id}-label` : undefined
  const InputComponent = variantComponent[variant]
  const InputElement = (
    <InputComponent
      aria-describedby={helperTextId}
      autoComplete={autoComplete}
      /* eslint-disable-next-line */
      autoFocus={autoFocus}
      defaultValue={defaultValue}
      fullWidth={fullWidth}
      multiline={multiline}
      name={name}
      rows={rows}
      maxRows={maxRows}
      minRows={minRows}
      type={type}
      id={id}
      inputRef={inputRef}
      onBlur={onBlur}
      /* @ts-ignore */
      onFocus={onFocus}
      placeholder={placeholder}
      inputProps={inputProps}
      {...(select !== 'advanceSelect' ? { value, onChange } : {})}
      {...InputMore}
      {...InputProps}
    />
  )

  return (
    <FormControl
      className={clsx(classes?.root, className)}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      ref={ref}
      required={required}
      color={color}
      variant={variant}
      {...other}
    >
      {label != null && label !== '' && (
        /* @ts-ignore */
        <InputLabel htmlFor={id} id={inputLabelId} {...InputLabelProps}>
          {label}
        </InputLabel>
      )}

      {select && select === 'advanceSelect' ? (
        <SelectAdvance<T>
          aria-describedby={helperTextId}
          id={id}
          input={InputElement}
          {...(SelectProps as SelectAdvanceProps<T>)}
          value={value as SelectAdvanceProps<T>['value']}
          onChange={onChange as SelectAdvanceProps<T>['onChange']}
          readOnly={InputProps?.readOnly}
        />
      ) : select ? (
        <Select
          aria-describedby={helperTextId}
          id={id}
          labelId={inputLabelId}
          value={value}
          input={InputElement}
          {...(SelectProps as SelectProps)}
          readOnly={InputProps?.readOnly}
        >
          {children}
        </Select>
      ) : (
        InputElement
      )}

      {helperText && (
        <FormHelperText id={helperTextId} {...FormHelperTextProps}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  )
}

export const TextField = React.forwardRef(TextFieldFn) as <T>(
  props: BaseTextFieldProps<T>,
  ref?: React.ForwardedRef<HTMLDivElement>
) => ReturnType<typeof TextFieldFn>
